/**
 * tenantDateTime — anchors the app's datetime runtime to the TENANT's own
 * configured timezone at boot.
 *
 * Why this exists (the defect it closes): every timestamp the API serves is a
 * correct offset-bearing ISO-8601 instant (`...Z`). Rendering it for a human
 * therefore requires a display zone, and `@v0y4ge/jrni-*`'s datetime runtime
 * (cd-app/lib/datetime.ts) defaults to the BROWSER's local zone when nothing
 * configures it. Until this ran, the only caller of `configureDateTime` was the
 * Settings -> General screen — a user-driven surface most sessions never open —
 * so the whole app formatted tenant data in whatever zone the viewer's laptop
 * happened to sit in. On an operator machine in the tenant's own zone that
 * looks correct and is correct by accident; for every other viewer it silently
 * renders the wrong wall-clock time.
 *
 * The zone comes from `getTenantTimeZone().timezone`, resolved server-side from
 * tenant config and readable by any signed-in member. It used to come from the
 * admin public-config read, which needs `settings.read`: a team member was
 * refused, so their whole session rendered in their device zone. It is NEVER
 * defaulted here: no fallback zone is applied, because a wrong-but-plausible
 * time is worse than an unanchored one, and the platform frontend carries no
 * tenant identity of its own. A tenant with no configured zone answers null.
 */
import { useEffect } from "react";
import { getTenantTimeZone, type StudioSettingsRequestOptions } from "@v0y4ge/api-client";
import { configureDateTime, reassertUserTimezonePreference } from "../cd-app/lib/datetime";

const OPTS: StudioSettingsRequestOptions = {
  baseUrl: ((import.meta.env.VITE_SETTINGS_API_URL as string | undefined) ?? "/api"),
  surface: "settings",
};

/** The session the applied zone belongs to; null until a lookup has succeeded. */
let appliedFor: string | null = null;
/** The session a lookup is currently running for. */
let inFlightFor: string | null = null;

/**
 * Fetch the tenant timezone for `sessionKey` and configure the datetime runtime with it.
 *
 * Keyed on the session rather than done once: a failed lookup is retried the next time a
 * session is presented, and a different tenant or person gets its own zone instead of
 * keeping the first one for the life of the page.
 */
export function applyTenantTimeZone(sessionKey = ""): void {
  if (appliedFor === sessionKey || inFlightFor === sessionKey || typeof getTenantTimeZone !== "function") return;
  inFlightFor = sessionKey;
  (getTenantTimeZone as (o: StudioSettingsRequestOptions) => Promise<unknown>)(OPTS)
    .then((res: unknown) => {
      // A newer session started its own lookup while this one ran: its answer wins.
      if (inFlightFor !== sessionKey) return;
      const raw = res as Record<string, unknown> | null;
      const body = (raw && typeof raw === "object" && "data" in raw ? raw.data : raw) as
        | { timezone?: unknown }
        | null;
      const tz = typeof body?.timezone === "string" ? body.timezone.trim() : "";
      if (!tz) {
        console.warn("tenant has no configured time zone; dates will render in the viewer local zone");
        return;
      }
      configureDateTime({ timeZone: tz });
      reassertUserTimezonePreference();
      appliedFor = sessionKey;
    })
    .catch((err: unknown) => {
      console.warn("tenant timezone lookup failed; dates will render in the viewer local zone:", err);
    })
    .finally(() => {
      if (inFlightFor === sessionKey) inFlightFor = null;
    });
}

/** React hook: applies the tenant timezone once `ready`, and again when `sessionKey` changes. */
export function useTenantTimeZone(ready: boolean, sessionKey = ""): void {
  useEffect(() => {
    if (!ready) return;
    applyTenantTimeZone(sessionKey);
  }, [ready, sessionKey]);
}

/** Test seam: forget the applied/in-flight state so a suite can re-drive boot. */
export function __resetTenantTimeZoneForTests(): void {
  appliedFor = null;
  inFlightFor = null;
}
